import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { AutomationWizardStepProps } from "../../../AutomationsUtils";
import PageFormWidget from "../../../../Forms/FormWizard/PageFormRenderer/PageFormWidget/PageFormWidget";
import FormWizardFormWrapper from "../../../../Forms/FormWizard/FormWizardFormWrapper";
import { CheckBox, CheckBoxVariant } from "@SeemplicityDev/shared";
import PageFormButtons from "../../../../Forms/FormWizard/PageFormRenderer/PageFormButtons/PageFormButtons";
import "./TicketMonitoringStep.scss";

import TicketSlaReminderConfig from "./TicketSlaReminderConfig";
import TicketReopenConfig from "./TicketReopenConfig";
import { TicketingQueueWizardFormFields } from "../TicketingQueueWizard";

const TICKETS_REOPEN_CONFIG_DEFAULT_VALUES: Pick<
  TicketingQueueWizardFormFields,
  | "ticketsReopenEnabled"
  | "ticketsReopenUnlimited"
  | "daysToReopenTickets"
  | "ticketsReopenStartThreshold"
> = {
  ticketsReopenEnabled: false,
  ticketsReopenUnlimited: false,
  daysToReopenTickets: 3,
  ticketsReopenStartThreshold: 90,
};

const TicketMonitoringStep: React.FC<AutomationWizardStepProps> = ({
  defaultValues,
  onValidate = () => {},
  isSummary = false,
}) => {
  const methods = useForm({
    mode: "all",
    defaultValues: {
      ...TICKETS_REOPEN_CONFIG_DEFAULT_VALUES,
      enableSlaReminder: false,
      ...defaultValues,
    },
  });
  const { handleSubmit, getValues, control } = methods;

  const ticketsReopenEnabled = useWatch({
    control,
    name: "ticketsReopenEnabled",
  });
  const enableSlaReminder = useWatch({
    control,
    name: "enableSlaReminder",
  });

  return (
    <FormProvider {...methods}>
      <FormWizardFormWrapper
        handleSubmit={handleSubmit}
        onSubmit={onValidate}
        getValues={getValues}
      >
        <PageFormWidget isSummary={isSummary}>
          <div
            className={`${
              isSummary ? "ticket-monitoring-step-container-summary" : ""
            }`}
          >
            {!isSummary && (
              <div className="ticket-monitoring-step-title">Ticket Reopen</div>
            )}
            <>
              {!isSummary && (
                <Controller
                  name={"ticketsReopenEnabled"}
                  render={({ field: { value, onChange } }) => (
                    <CheckBox
                      data-testid={"should-reopen-tickets"}
                      checked={value}
                      onChange={(_val: boolean, newValue: boolean) =>
                        onChange(newValue)
                      }
                      label="Enable Ticket Reopen"
                      variant={CheckBoxVariant.TOGGLE}
                    />
                  )}
                  control={control}
                />
              )}
              {!isSummary && (
                <div className="ticket-monitoring-step-description">
                  Automatically reopens tickets when the associated finding is
                  not fixed within the configured time period.
                </div>
              )}
            </>
            {ticketsReopenEnabled && (
              <TicketReopenConfig isSummary={isSummary} />
            )}

            <div
              className={`${!isSummary ? "ticket-sla-reminders-wrapper" : ""}`}
            >
              {!isSummary && (
                <div className="ticket-monitoring-step-title">
                  Ticket SLA Reminders
                </div>
              )}

              <>
                {!(enableSlaReminder && isSummary) && (
                  <Controller
                    name={`enableSlaReminder`}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <CheckBox
                          name={`enableSlaReminder`}
                          data-testid="enableSlaReminder"
                          checked={value}
                          onChange={(_val: boolean, newValue: boolean) =>
                            onChange(newValue)
                          }
                          label={`${
                            isSummary ? "Ticket" : "Enable"
                          } SLA Reminders`}
                          variant={CheckBoxVariant.TOGGLE}
                          viewMode={isSummary}
                        />
                      );
                    }}
                    control={control}
                  />
                )}
                {!isSummary && (
                  <div className="ticket-monitoring-step-description">
                    Sends automatic reminders to ticket assignees as comments
                    when their tickets are approaching the SLA deadline.
                    <br />
                    This requires tickets to have a due date, which is only
                    available if{" "}
                    <a
                      href={"/policies/ticket-sla-rules"}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Ticket SLA Rules
                    </a>{" "}
                    are set up.
                  </div>
                )}
              </>

              {!!enableSlaReminder && (
                <TicketSlaReminderConfig isSummary={isSummary} />
              )}
            </div>
          </div>
        </PageFormWidget>
        {!isSummary && (
          <PageFormButtons data-testid="monitoring-step-next-button" />
        )}
      </FormWizardFormWrapper>
    </FormProvider>
  );
};

export default TicketMonitoringStep;
